/**
 * Chutty PDP — thumbnail gallery, share link, ask panel, buy now
 */
(function () {
  function initMediaGallery(root) {
    const gallery = root.querySelector('[data-chutty-pdp-media]');
    if (!gallery) return;

    const thumbs = gallery.querySelectorAll('.chutty-pdp__thumb');
    const panels = gallery.querySelectorAll('.chutty-pdp__media-panel');
    const thumbViewport = gallery.querySelector('[data-chutty-thumbs-viewport]');
    const thumbPrev = gallery.querySelector('[data-chutty-thumb-prev]');
    const thumbNext = gallery.querySelector('[data-chutty-thumb-next]');

    const getThumbStep = () => 82;

    const updateThumbNav = () => {
      if (!thumbPrev || !thumbNext) return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (isMobile) {
        thumbPrev.disabled = false;
        thumbNext.disabled = false;
        return;
      }

      if (!thumbViewport) return;

      const maxScroll = thumbViewport.scrollHeight - thumbViewport.clientHeight;
      if (maxScroll <= 1) {
        thumbPrev.disabled = false;
        thumbNext.disabled = false;
        return;
      }

      thumbPrev.disabled = thumbViewport.scrollTop <= 1;
      thumbNext.disabled = thumbViewport.scrollTop >= maxScroll - 1;
    };

    const scrollThumbs = (direction) => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      if (isMobile) {
        goByDirection(direction);
        return;
      }

      if (!thumbViewport) {
        goByDirection(direction);
        return;
      }

      const maxScroll = thumbViewport.scrollHeight - thumbViewport.clientHeight;
      if (maxScroll <= 1) {
        goByDirection(direction);
        return;
      }

      thumbViewport.scrollBy({
        top: direction * getThumbStep(),
        left: 0,
        behavior: 'smooth',
      });
    };

    if (thumbPrev || thumbNext) {
      thumbPrev?.addEventListener('click', () => scrollThumbs(-1));
      thumbNext?.addEventListener('click', () => scrollThumbs(1));
    }

    if (thumbViewport) {
      thumbViewport.addEventListener('scroll', updateThumbNav, { passive: true });
      window.addEventListener('resize', updateThumbNav);
    }

    updateThumbNav();

    const panelsVisible = Array.prototype.some.call(panels, (panel) => !panel.hidden);
    if (!panelsVisible && panels[0]) {
      panels[0].hidden = false;
      panels[0].classList.add('is-active');
    }

    const activateByIndex = (index) => {
      const thumb = thumbs[index];
      if (!thumb) return false;

      const targetId = thumb.dataset.mediaTarget;
      if (!targetId) return false;

      thumbs.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      panels.forEach((panel) => {
        const active = panel.id === targetId;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      thumb.classList.add('is-active');
      thumb.setAttribute('aria-selected', 'true');

      if (thumbViewport) {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        window.requestAnimationFrame(updateThumbNav);
      }

      return true;
    };

    const getActiveIndex = () => {
      const activeIndex = Array.prototype.findIndex.call(thumbs, (thumb) =>
        thumb.classList.contains('is-active')
      );
      return activeIndex >= 0 ? activeIndex : 0;
    };

    const getWrappedIndex = (index) => {
      if (!thumbs.length) return -1;
      return (index + thumbs.length) % thumbs.length;
    };

    const goByDirection = (direction) => {
      if (thumbs.length < 2) return false;
      const current = getActiveIndex();
      const next = getWrappedIndex(current + direction);
      return activateByIndex(next);
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        activateByIndex(index);
      });
    });

    // Mobile: swipe main image to previous / next
    const mainMedia = gallery.querySelector('.chutty-pdp__main-media');
    if (mainMedia && thumbs.length > 1) {
      let touchStartX = 0;
      let touchStartY = 0;
      let touchActive = false;

      const isMobileSwipe = () => window.matchMedia('(max-width: 767px)').matches;

      mainMedia.addEventListener(
        'touchstart',
        (event) => {
          if (!isMobileSwipe() || event.touches.length !== 1) return;
          touchActive = true;
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
        },
        { passive: true }
      );

      mainMedia.addEventListener(
        'touchend',
        (event) => {
          if (!touchActive || !isMobileSwipe()) return;
          touchActive = false;

          const touch = event.changedTouches[0];
          if (!touch) return;

          const dx = touch.clientX - touchStartX;
          const dy = touch.clientY - touchStartY;
          const absX = Math.abs(dx);
          const absY = Math.abs(dy);

          // Horizontal swipe only; ignore taps and vertical scrolls
          if (absX < 40 || absX < absY * 1.2) return;

          const moved = dx < 0 ? goByDirection(1) : goByDirection(-1);
          if (!moved) return;

          // Prevent PhotoSwipe opening from the same gesture
          gallery.dataset.chuttySwiped = 'true';
          window.setTimeout(() => {
            delete gallery.dataset.chuttySwiped;
          }, 350);
        },
        { passive: true }
      );

      mainMedia.addEventListener(
        'touchcancel',
        () => {
          touchActive = false;
        },
        { passive: true }
      );
    }
  }

  function initShare(root) {
    const shareBtn = root.querySelector('[data-chutty-share]');
    if (!shareBtn) return;

    shareBtn.addEventListener('click', async () => {
      const url = shareBtn.dataset.shareUrl || window.location.href;
      const title = shareBtn.dataset.shareTitle || document.title;

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (err) {
          if (err.name === 'AbortError') return;
        }
      }

      try {
        await navigator.clipboard.writeText(url);
        const label = shareBtn.querySelector('[data-share-label]');
        if (label) {
          const original = label.textContent;
          label.textContent = 'Link copied';
          setTimeout(() => {
            label.textContent = original;
          }, 2000);
        }
      } catch (_e) {
        window.prompt('Copy this link:', url);
      }
    });
  }

  function initAskQuestion(root) {
    const toggle = root.querySelector('[data-chutty-ask-toggle]');
    const panel = root.querySelector('[data-chutty-ask-panel]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  function initBuyNow(root) {
    const buyNowBtn = root.querySelector('[data-chutty-buy-now]');
    const form = root.querySelector('.chutty-pdp__form');
    if (!buyNowBtn || !form) return;

    buyNowBtn.addEventListener('click', async () => {
      if (buyNowBtn.disabled || buyNowBtn.classList.contains('is-loading')) return;

      const variantInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector('input[name="quantity"]');
      const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
      const quantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value || 1) : 1;

      if (!variantId) return;

      buyNowBtn.classList.add('is-loading');
      buyNowBtn.disabled = true;

      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const checkoutUrl = rootUrl + 'checkout';

      try {
        const response = await fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: quantity }],
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.description || data?.message || 'Unable to add item to cart');
        }

        window.location.href = checkoutUrl;
      } catch (error) {
        buyNowBtn.classList.remove('is-loading');
        buyNowBtn.disabled = form.querySelector('.chutty-pdp__atc')?.disabled ?? false;
        console.error('[chutty-pdp] Buy now failed:', error);
      }
    });
  }

  function initSizeChart(root) {
    const openBtn = root.querySelector('[data-chutty-size-chart-open]');
    const modal = root.querySelector('[data-chutty-size-chart-modal]');
    if (!openBtn || !modal) return;

    const closeButtons = modal.querySelectorAll('[data-chutty-size-chart-close]');
    const panel = modal.querySelector('.chutty-size-chart-modal__panel');

    const closeModal = () => {
      modal.hidden = true;
      document.body.style.overflow = '';
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.focus();
    };

    const openModal = () => {
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      openBtn.setAttribute('aria-expanded', 'true');
    };

    openBtn.addEventListener('click', openModal);
    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (event) => {
      if (panel && !panel.contains(event.target)) {
        closeModal();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });
  }

  function syncBuyNowState(root) {
    const buyNowBtn = root.querySelector('[data-chutty-buy-now]');
    const atcBtn = root.querySelector('.chutty-pdp__atc');
    if (!buyNowBtn || !atcBtn) return;
    buyNowBtn.disabled = atcBtn.disabled;
  }

  function initVariantInstantFeedback(root) {
    const picker = root.querySelector('variant-picker.chutty-pdp__variant-picker');
    if (!picker) return;

    const sectionId = picker.dataset.sectionId || '';
    let variantMediaMap = {};
    const mediaMapScript = picker.querySelector('[data-chutty-variant-media]');
    if (mediaMapScript && mediaMapScript.textContent) {
      try {
        variantMediaMap = JSON.parse(mediaMapScript.textContent);
      } catch (error) {
        variantMediaMap = {};
      }
    }

    const activateMediaById = (mediaPanelId) => {
      if (!mediaPanelId) return;
      const gallery = root.querySelector('[data-chutty-pdp-media]');
      if (!gallery) return;

      const targetPanel = document.getElementById(mediaPanelId);
      if (!(targetPanel instanceof HTMLElement) || !gallery.contains(targetPanel)) return;

      const thumbs = gallery.querySelectorAll('.chutty-pdp__thumb');
      const panels = gallery.querySelectorAll('.chutty-pdp__media-panel');

      thumbs.forEach((t) => {
        const active = t.dataset.mediaTarget === mediaPanelId;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach((panel) => {
        const active = panel.id === mediaPanelId;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      const matchingThumb = Array.prototype.find.call(
        thumbs,
        (thumb) => thumb.dataset.mediaTarget === mediaPanelId
      );
      matchingThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    const activateMediaForVariant = (variantId, fallbackPanelId) => {
      const mediaId = variantId ? variantMediaMap[String(variantId)] : null;
      if (mediaId) {
        activateMediaById(`${sectionId}-media-${mediaId}`);
        return;
      }
      activateMediaById(fallbackPanelId);
    };

    const updateFieldsetUI = (input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const fieldset = input.closest('.chutty-pdp__variant-fieldset');
      if (!fieldset) return;

      const labels = fieldset.querySelectorAll('.chutty-pdp__variant-label');
      labels.forEach((label) => label.classList.remove('is-selected'));
      const activeLabel = input.closest('.chutty-pdp__variant-label');
      if (activeLabel) activeLabel.classList.add('is-selected');

      const selectedText = fieldset.querySelector('.chutty-pdp__variant-selected');
      if (selectedText) selectedText.textContent = input.value;

      const mediaId = input.dataset.optionMediaId || (activeLabel && activeLabel.dataset.mediaId);
      activateMediaForVariant(input.dataset.variantId, mediaId);
    };

    picker.querySelectorAll('.chutty-pdp__variant-fieldset').forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedInput instanceof HTMLInputElement) {
        const labels = fieldset.querySelectorAll('.chutty-pdp__variant-label');
        labels.forEach((label) => label.classList.remove('is-selected'));
        const activeLabel = checkedInput.closest('.chutty-pdp__variant-label');
        if (activeLabel) activeLabel.classList.add('is-selected');
        const selectedText = fieldset.querySelector('.chutty-pdp__variant-selected');
        if (selectedText) selectedText.textContent = checkedInput.value;
      }
    });

    picker.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
      updateFieldsetUI(target);
    });

    const section = root.closest('.shopify-section');
    if (section) {
      section.addEventListener('product:select', (event) => {
        const promise = event.promise;
        if (!promise || typeof promise.then !== 'function') return;
        promise
          .then((result) => {
            const resource = result && result.detail && result.detail.resource;
            if (!resource) return;
            const featuredId = resource.featured_media && resource.featured_media.id;
            if (featuredId) {
              activateMediaById(`${sectionId}-media-${featuredId}`);
              return;
            }
            activateMediaForVariant(resource.id);
          })
          .catch(() => {});
      });
    }
  }

  function initVariantSync(root) {
    const section = root.closest('.shopify-section');
    if (!section) return;

    section.addEventListener('product:select', (event) => {
      const promise = event.promise;
      if (!promise || typeof promise.finally !== 'function') return;
      promise.finally(() => {
        syncBuyNowState(root);
      });
    });

    const atcBtn = root.querySelector('.chutty-pdp__atc');
    if (atcBtn) {
      const observer = new MutationObserver(() => {
        syncBuyNowState(root);
      });
      observer.observe(atcBtn, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  function initStickyAtc(root) {
    const sticky = root.querySelector('[data-chutty-sticky-atc]');
    if (!sticky) return;

    const buyActions = root.querySelector('.chutty-pdp__buy-actions');
    const form = root.querySelector('.chutty-pdp__form');
    const mainAtc = root.querySelector('.chutty-pdp__atc');
    const variantInput = form && form.querySelector('input[name="id"]');
    const mainQtyInput = form && form.querySelector('input[name="quantity"]');
    const stickyVariant = sticky.querySelector('[data-chutty-sticky-variant]');
    const stickyQtyInput = sticky.querySelector('[data-chutty-sticky-qty-input]');
    const stickyMinus = sticky.querySelector('[data-chutty-sticky-qty-minus]');
    const stickyPlus = sticky.querySelector('[data-chutty-sticky-qty-plus]');
    const stickyAtcBtn = sticky.querySelector('[data-chutty-sticky-atc-btn]');
    const stickyImage = sticky.querySelector('[data-chutty-sticky-image]');

    const syncStickyOffset = (stuck) => {
      if (!stuck) {
        document.body.classList.remove('chutty-sticky-atc-active');
        document.documentElement.style.removeProperty('--chutty-sticky-atc-height');
        return;
      }

      document.body.classList.add('chutty-sticky-atc-active');
      document.documentElement.style.setProperty('--chutty-sticky-atc-height', `${sticky.offsetHeight}px`);
    };

    const setStuck = (stuck) => {
      sticky.dataset.stuck = stuck ? 'true' : 'false';
      sticky.setAttribute('aria-hidden', stuck ? 'false' : 'true');
      syncStickyOffset(stuck);
    };

    if (buyActions && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          const rect = entry.boundingClientRect;
          const scrolledPast = !entry.isIntersecting && rect.bottom < 0;
          setStuck(scrolledPast);
        },
        { threshold: 0 }
      );
      observer.observe(buyActions);
    } else {
      setStuck(true);
    }

    window.addEventListener('resize', () => {
      if (sticky.dataset.stuck === 'true') {
        syncStickyOffset(true);
      }
    });

    const syncStickyAvailability = (available) => {
      sticky.dataset.available = available ? 'true' : 'false';
      if (stickyAtcBtn) stickyAtcBtn.disabled = !available;
    };

    const syncMainQtyFromSticky = () => {
      if (!stickyQtyInput || !mainQtyInput) return;
      const min = Number(stickyQtyInput.min || 1);
      let value = Number(stickyQtyInput.value || min);
      if (Number.isNaN(value) || value < min) value = min;
      stickyQtyInput.value = String(value);
      mainQtyInput.value = String(value);
      mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const syncStickyQtyFromMain = () => {
      if (!stickyQtyInput || !mainQtyInput) return;
      stickyQtyInput.value = mainQtyInput.value || stickyQtyInput.min || '1';
    };

    if (stickyMinus) {
      stickyMinus.addEventListener('click', () => {
        if (!stickyQtyInput) return;
        const min = Number(stickyQtyInput.min || 1);
        stickyQtyInput.value = String(Math.max(min, Number(stickyQtyInput.value || min) - 1));
        syncMainQtyFromSticky();
      });
    }

    if (stickyPlus) {
      stickyPlus.addEventListener('click', () => {
        if (!stickyQtyInput) return;
        const min = Number(stickyQtyInput.min || 1);
        stickyQtyInput.value = String(Number(stickyQtyInput.value || min) + 1);
        syncMainQtyFromSticky();
      });
    }

    if (stickyQtyInput) {
      stickyQtyInput.addEventListener('change', syncMainQtyFromSticky);
    }

    if (mainQtyInput) {
      mainQtyInput.addEventListener('change', syncStickyQtyFromMain);
      mainQtyInput.addEventListener('input', syncStickyQtyFromMain);
      syncStickyQtyFromMain();
    }

    const applyVariantId = (variantId) => {
      if (!variantId || !variantInput) return;
      variantInput.value = String(variantId);
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));

      // Mirror selection into option radios when possible
      const option = stickyVariant && stickyVariant.selectedOptions[0];
      if (option) {
        const title = option.dataset.title || '';
        const parts = title.split(' / ').map((part) => part.trim());
        const fieldsets = root.querySelectorAll('.chutty-pdp__variant-fieldset');
        fieldsets.forEach((fieldset, index) => {
          const value = parts[index];
          if (!value) return;
          const input = Array.prototype.find.call(
            fieldset.querySelectorAll('input[type="radio"]'),
            (radio) => radio.value === value
          );
          if (input && !input.checked && !input.disabled) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    };

    if (stickyVariant) {
      stickyVariant.addEventListener('change', () => {
        const option = stickyVariant.selectedOptions[0];
        if (!option) return;
        applyVariantId(stickyVariant.value);
        syncStickyAvailability(option.dataset.available === 'true');
        if (stickyImage && option.dataset.image) {
          stickyImage.src = option.dataset.image;
        }
      });
    }

    // Keep sticky select in sync when main radios change
    const picker = root.querySelector('variant-picker.chutty-pdp__variant-picker');
    if (picker && stickyVariant) {
      picker.addEventListener('change', () => {
        window.setTimeout(() => {
          if (!(variantInput instanceof HTMLInputElement)) return;
          const nextId = variantInput.value;
          if (!nextId) return;
          if (stickyVariant.value !== nextId) {
            const match = stickyVariant.querySelector('option[value="' + nextId + '"]');
            if (match) {
              stickyVariant.value = nextId;
              syncStickyAvailability(match.dataset.available === 'true');
              if (stickyImage && match.dataset.image) stickyImage.src = match.dataset.image;
            }
          }
        }, 50);
      });
    }

    if (stickyAtcBtn) {
      stickyAtcBtn.addEventListener('click', () => {
        syncMainQtyFromSticky();
        if (mainAtc && !mainAtc.disabled) {
          mainAtc.click();
          return;
        }
        if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }

    if (mainAtc) {
      const observer = new MutationObserver(() => {
        syncStickyAvailability(!mainAtc.disabled);
      });
      observer.observe(mainAtc, { attributes: true, attributeFilter: ['disabled'] });
      syncStickyAvailability(!mainAtc.disabled);
    }
  }

  function initSection(section) {
    initMediaGallery(section);
    initShare(section);
    initAskQuestion(section);
    initBuyNow(section);
    initSizeChart(section);
    initVariantInstantFeedback(section);
    initVariantSync(section);
    initStickyAtc(section);
    syncBuyNowState(section);
  }

  document.querySelectorAll('.chutty-pdp').forEach(initSection);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.chutty-pdp') || event.target.closest('.chutty-pdp');
    if (section) initSection(section);
  });
})();
